import React from "react";
import { Box, SxProps, Theme } from "@mui/material";

import { Account, GuestAccount } from "fond/types";
import { isDataImage } from "fond/utils/image";

import Avatar, { AvatarProps } from "./Avatar";

interface AccountAvatarProps {
  /** The account to display - Account (member) or GuestAccount (guest with branding) */
  account: Account | GuestAccount;
  /** Optional className for styling */
  className?: string;
  /** Optional sx props for styling */
  sx?: SxProps<Theme>;
  /** Avatar variant - defaults to "rounded" */
  variant?: AvatarProps["variant"];
  /** Optional test id */
  "data-testid"?: string;
}

const AccountAvatar: React.FC<AccountAvatarProps> = ({ account, className, sx, variant = "rounded", "data-testid": dataTestId }) => {
  const defaultSx: SxProps<Theme> = {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
  };
  // If logo is set, display it as an image (available on both Account and GuestAccount)
  if (account.Logo && isDataImage(account.Logo)) {
    return (
      <Box
        className={className}
        sx={{
          ...defaultSx,
          backgroundImage: `url(${account.Logo})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          ...(variant === "rounded" ? { borderRadius: 1 } : {}),
          ...(variant === "circular" ? { borderRadius: "50%" } : {}),
          ...sx,
        }}
        data-testid={dataTestId}
      />
    );
  }

  const defaultProps: AvatarProps = {
    className,
    variant,
    maxInitials: 3,
    name: account.Name,
    "data-testid": dataTestId,
  };

  // If custom colors are set, use them (available on both Account and GuestAccount)
  if (account.BackgroundColor && account.ForegroundColor) {
    return (
      <Avatar
        {...defaultProps}
        sx={{
          ...defaultSx,
          bgcolor: account.BackgroundColor,
          color: account.ForegroundColor,
          ...sx,
        }}
      />
    );
  }

  // Fall back to default Avatar with stringColor
  return <Avatar {...defaultProps} stringColor sx={{ ...defaultSx, ...sx }} />;
};

export default AccountAvatar;
